import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import {
  Link,
  Stack,
  Checkbox,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel
} from '@mui/material';
import Alert from '@mui/material/Alert';
import { LoadingButton } from '@mui/lab';
import { api } from '../../../Api';
import Iconify from '../../../components/Iconify';
import { postJson } from 'src/utils/clutchApi';
import { getDefaultDashboardPath, storeAdminSession } from 'src/utils/clutchAdminAuth';

export default function LoginForm() {
  const [alert, setalert] = useState({ visible: false, message: '' });
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setisSubmitting] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Enter a valid email address').required('Email address is required'),
    password: Yup.string().required('Password is required')
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      remember: false
    },
    validationSchema: LoginSchema,
    onSubmit: () => {
      login();
    }
  });

  const { errors, touched, values, handleSubmit, getFieldProps, setFieldValue } = formik;

  const savedEmail = localStorage.getItem('email');
  const savedPassword = localStorage.getItem('password');
  const rememberMe = localStorage.getItem('remember') === 'true';

  useEffect(() => {
    if (savedEmail && savedPassword) {
      setFieldValue('email', savedEmail);
      setFieldValue('password', savedPassword);
      setFieldValue('remember', rememberMe);
    }
  }, [rememberMe, savedEmail, savedPassword, setFieldValue]);

  const login = async () => {
    setisSubmitting(true);
    setalert({ visible: false, message: '' });

    try {
      const result = await postJson(api.login, {
        email: values.email,
        password: values.password
      });

      const payload = result.data || {};
      storeAdminSession({
        user: payload.user,
        accessToken: payload.accessToken,
        permissions: payload.permissions
      });

      if (values.remember) {
        localStorage.setItem('email', values.email);
        localStorage.setItem('password', values.password);
        localStorage.setItem('remember', 'true');
      } else {
        localStorage.removeItem('email');
        localStorage.removeItem('password');
        localStorage.removeItem('remember');
      }

      navigate(getDefaultDashboardPath(), { replace: true });
    } catch (error) {
      setalert({ visible: true, message: error.message || 'Login failed.' });
    } finally {
      setisSubmitting(false);
    }
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            autoComplete="username"
            type="email"
            label="Email address"
            {...getFieldProps('email')}
            error={Boolean(touched.email && errors.email)}
            helperText={touched.email && errors.email}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? 'text' : 'password'}
            label="Password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((show) => !show)} edge="end">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
          />
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
          <FormControlLabel
            control={<Checkbox {...getFieldProps('remember')} checked={values.remember} />}
            label="Remember me"
          />
          <Link component={RouterLink} variant="subtitle2" to="/forgot" underline="hover" sx={{ color: '#3F90DF' }}>
            Forgot password?
          </Link>
        </Stack>

        {alert.visible && (
          <Alert
            onClose={() => {
              setalert({ visible: false, message: '' });
            }}
            severity="error"
            sx={{ fontSize: 12, mb: 2.5 }}
          >
            {alert.message}
          </Alert>
        )}

        <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting}>
          Login
        </LoadingButton>
      </Form>
    </FormikProvider>
  );
}
